import { useCallback, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownToLine, ChevronDown, ChevronUp, Image as ImageIcon, MessageSquareText, Plus } from 'lucide-react';
import { Textarea } from '@repo/ui/src/components/ui/textarea';
import { cn } from '@repo/ui/src/lib/utils';
import type { NoteTemplate, SessionRating } from '@repo/shared-types';
import { sessionRatingMeta } from '@repo/shared-types';
import { SessionRatingPicker } from './SessionRatingPicker';
import { NoteItemTable, type OrphanItem } from './NoteItemTable';
import { hasAnyText, photoCount, previewParts, type DraftPhoto, type NoteDraft } from './noteDraft';
import { HomeworkPhotoAddButton, HomeworkPhotoStrip, type HomeworkPhotoStripHandle } from './HomeworkPhotoStrip';

export interface NotesRoster {
  studentId: string;
  studentName: string;
  studentNickname: string | null;
  attendance: 'PRESENT' | 'ABSENT' | 'EXCUSED' | 'PENDING';
}

/**
 * The form body, identical in the phone Drawer and the desktop Sheet — and
 * deliberately presentational. ALL note text lives in `SessionNotesPanel`,
 * the one component that stays mounted regardless of which container
 * (Drawer or Sheet) currently wraps this form: `useIsMobile()` resolves
 * AFTER mount, so the container can flip once right after opening, and
 * hooks tied to a child of that container would lose their state on the
 * swap. Hooks tied to the panel itself do not (port guide §2.6, and the
 * review that found the Form previously owning this state).
 *
 * THI-193 (E2): when the subject has a template, the class group renders one
 * label-above textarea per class item, common homework and the per-student
 * groups render the label-left `NoteItemTable`, and each student gets a
 * collapsed "Ghi chú ngoài sườn" free textarea. With `template === null` the
 * form is exactly the cycle-3 D1 form.
 *
 * Collapsing a row only hides it — `hidden`, never a conditional unmount —
 * and no `key` changes with the open student, the same `key={step}` trap
 * the landing hit (port guide §2.2). The only local state here is UI-only
 * (class group collapsed, extra-note links opened), safe to lose on a swap.
 */
/** The one-line explainer under a field label: what the field feeds (the
 *  parent page, the AI monthly draft). Its own line, never inline with the
 *  label, so on a 390px phone it can wrap without pushing "Thêm ảnh" off
 *  the label row. */
function FieldHint({ children }: { children: ReactNode }) {
  return <p className="mb-1.5 text-[12px] leading-snug text-surface-400">{children}</p>;
}

export function SessionNotesForm({
  roster,
  showClassTier,
  template,
  ratingEnabled,
  classDraft,
  onClassCommentChange,
  onClassHomeworkChange,
  onClassItemChange,
  onClassHomeworkItemChange,
  onCopyDown,
  students,
  onStudentCommentChange,
  onStudentHomeworkChange,
  onStudentRatingChange,
  onStudentItemChange,
  onStudentHomeworkItemChange,
  orphansByStudent,
  homeworkOrphansByStudent,
  classOrphans,
  classHomeworkOrphans,
  sessionId,
  photoSubtitle,
  planAllowsPhotos,
  onClassPhotosChange,
  onStudentPhotosChange,
  openStudentId,
  onToggleStudent,
  focusStudentId,
  focusRef,
}: {
  roster: NotesRoster[];
  showClassTier: boolean;
  template: NoteTemplate | null;
  /** THI-346: `Subject.sessionRatingEnabled`; false hides the picker. */
  ratingEnabled: boolean;
  classDraft: NoteDraft;
  onClassCommentChange: (value: string) => void;
  onClassHomeworkChange: (value: string) => void;
  onClassItemChange: (itemId: string, value: string) => void;
  onClassHomeworkItemChange: (itemId: string, value: string) => void;
  onCopyDown: () => void;
  students: Record<string, NoteDraft>;
  onStudentCommentChange: (studentId: string, value: string) => void;
  onStudentHomeworkChange: (studentId: string, value: string) => void;
  onStudentRatingChange: (studentId: string, value: SessionRating | null) => void;
  onStudentItemChange: (studentId: string, itemId: string, value: string) => void;
  onStudentHomeworkItemChange: (studentId: string, itemId: string, value: string) => void;
  /** Saved items whose template row is gone, per student (snapshot names). */
  orphansByStudent: Record<string, OrphanItem[]>;
  homeworkOrphansByStudent: Record<string, OrphanItem[]>;
  classOrphans: OrphanItem[];
  classHomeworkOrphans: OrphanItem[];
  /** THI-196 photos: the session the presign is minted for, the lightbox
   *  subtitle, the three-state plan gate (§4.4), and the two setters. */
  sessionId: string;
  photoSubtitle: string;
  planAllowsPhotos: boolean | undefined;
  onClassPhotosChange: (next: DraftPhoto[]) => void;
  onStudentPhotosChange: (studentId: string, next: DraftPhoto[]) => void;
  openStudentId: string | null;
  onToggleStudent: (studentId: string) => void;
  focusStudentId?: string | null;
  focusRef: RefObject<HTMLTextAreaElement | null>;
}) {
  const { t } = useTranslation('progress');
  // Attendance-status labels ("Vắng"/"Có phép"/"Có mặt") already live in the
  // scheduling namespace — AttendanceRow reads the same keys — so this form
  // reuses them rather than forking a second Vietnamese string for the same
  // meaning.
  const { t: ts } = useTranslation('scheduling');
  const [classCollapsed, setClassCollapsed] = useState(false);
  const [extraOpen, setExtraOpen] = useState<Record<string, boolean>>({});
  const [classExtraOpen, setClassExtraOpen] = useState(false);
  // THI-196: one strip handle per tier so the label-row button can open it;
  // `stripShown` hides that button while the strip itself is visible.
  const classStrip = useRef<HomeworkPhotoStripHandle>(null);
  const studentStrips = useRef<Record<string, HomeworkPhotoStripHandle | null>>({});
  const [stripShown, setStripShown] = useState<Record<string, boolean>>({});
  const markShown = useCallback((key: string, v: boolean) => setStripShown((m) => (m[key] === v ? m : { ...m, [key]: v })), []);
  const onClassStripShown = useCallback((v: boolean) => markShown('class', v), [markShown]);

  const hasTemplate = template !== null;
  const classItems = template?.classItems ?? [];
  const studentItems = template?.studentItems ?? [];
  const homeworkItems = template?.homeworkItems ?? [];
  // One layout rule per field, decided from the group that owns it (review
  // I2/I3): a sectioned field gets its table, an unsectioned one keeps its
  // free textarea as the main control, and the "Ghi chú ngoài sườn" hatch
  // offers ONLY the free fields that a table has displaced, so a field is
  // never rendered twice and never left without an editor.
  const classUsesItems = hasTemplate && classItems.length > 0;
  const classUsesHw = hasTemplate && homeworkItems.length > 0;
  const classHatch = classUsesItems || classUsesHw;
  const classExtraShown = classExtraOpen
    || (classUsesItems && Boolean(classDraft.comment.trim()))
    || (classUsesHw && Boolean(classDraft.homework.trim()));

  const writtenCount = roster.filter((s) => hasAnyText(students[s.studentId])).length;
  const textareaClass = 'rounded-md border-surface-300 bg-surface-0 px-4 py-3 text-[15px] leading-[1.45] md:text-sm';

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-1">
      {showClassTier && (
        <section>
          <div className="mb-2.5 flex items-center gap-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-surface-400">
              {t('notes.class.label')}
            </span>
            <span className="h-px flex-1 bg-surface-200" />
            {hasTemplate && (
              <button
                type="button"
                onClick={() => setClassCollapsed((c) => !c)}
                className="inline-flex min-h-[28px] items-center gap-1 px-1 text-[12px] font-medium text-surface-500"
                aria-expanded={!classCollapsed}
              >
                {classCollapsed ? <ChevronDown className="h-[13px] w-[13px]" strokeWidth={2} /> : <ChevronUp className="h-[13px] w-[13px]" strokeWidth={2} />}
                {classCollapsed ? t('notes.class.expand') : t('notes.class.collapse')}
              </button>
            )}
          </div>
          {/* `hidden`, never unmounted: collapsing keeps the text. */}
          <div className={cn(classCollapsed && 'hidden')}>
            {classUsesItems ? (
              <>
                {classItems.map((it, idx) => (
                  <div key={it.id}>
                    <label className={cn('mb-1.5 block text-[12.5px] font-medium text-surface-600', idx > 0 && 'mt-2')} htmlFor={`note-class-item-${it.id}`}>
                      {it.name}
                    </label>
                    <Textarea
                      id={`note-class-item-${it.id}`}
                      value={classDraft.items[it.id] ?? ''}
                      onChange={(e) => onClassItemChange(it.id, e.target.value)}
                      placeholder={it.hint ?? ''}
                      className={cn(idx === 0 ? 'min-h-[48px]' : 'min-h-[40px]', textareaClass)}
                    />
                  </div>
                ))}
                {classOrphans.length > 0 && (
                  <div className="mt-2">
                    <NoteItemTable items={[]} values={classDraft.items} onChange={onClassItemChange} orphans={classOrphans} idPrefix="note-class-item" />
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Orphans stay visible (and clearable) even when this tier
                    has no sections any more, e.g. after "Bỏ sườn" (review I4). */}
                {classOrphans.length > 0 && (
                  <div className="mb-2">
                    <NoteItemTable items={[]} values={classDraft.items} onChange={onClassItemChange} orphans={classOrphans} idPrefix="note-class-item" />
                  </div>
                )}
                <label className="block text-[12.5px] font-medium text-surface-600" htmlFor="note-class-comment">
                  {t('notes.class.comment')}
                </label>
                <FieldHint>{t('notes.class.commentHint')}</FieldHint>
                <Textarea
                  id="note-class-comment"
                  value={classDraft.comment}
                  onChange={(e) => onClassCommentChange(e.target.value)}
                  placeholder={t('notes.class.commentPh')}
                  /* 15px on phones so iOS does not zoom the viewport on focus. */
                  className={cn('min-h-[66px]', textareaClass)}
                />
              </>
            )}
            {/* Two different item lists have nothing to copy between them (handoff 19 §5). */}
            {!hasTemplate && (
              <button
                type="button"
                onClick={onCopyDown}
                disabled={!classDraft.comment.trim()}
                className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 px-1 text-[12.5px] font-medium text-brand-600 disabled:opacity-50"
              >
                <ArrowDownToLine className="h-[13px] w-[13px]" strokeWidth={2} />
                {t('notes.class.copyDown')}
              </button>
            )}
            {classUsesHw ? (
              <>
                <div className="mb-1.5 mt-2.5 flex min-h-6 items-center text-[12.5px] font-medium text-surface-600">
                  {t('notes.homework.common')}
                  <HomeworkPhotoAddButton onOpen={() => classStrip.current?.openAdd()} planAllows={planAllowsPhotos} hidden={Boolean(stripShown.class)} />
                </div>
                <NoteItemTable
                  items={homeworkItems} values={classDraft.homeworkItems} onChange={onClassHomeworkItemChange} orphans={classHomeworkOrphans} idPrefix="note-class-hw"
                  footer={
                    <HomeworkPhotoStrip
                      ref={classStrip} photos={classDraft.photos} onChange={onClassPhotosChange}
                      sessionId={sessionId} tier="CLASS" scopeLabel={t('lightbox.common')} subtitle={photoSubtitle}
                      variant="card-row" onVisibilityChange={onClassStripShown}
                    />
                  }
                />
              </>
            ) : (
              <>
                {classHomeworkOrphans.length > 0 && (
                  <div className="mt-2.5">
                    <NoteItemTable items={[]} values={classDraft.homeworkItems} onChange={onClassHomeworkItemChange} orphans={classHomeworkOrphans} idPrefix="note-class-hw" />
                  </div>
                )}
                <label className="mb-1.5 mt-2.5 flex min-h-6 items-center text-[12.5px] font-medium text-surface-600" htmlFor="note-class-hw-free">
                  {t('notes.class.homework')}
                  <HomeworkPhotoAddButton onOpen={() => classStrip.current?.openAdd()} planAllows={planAllowsPhotos} hidden={Boolean(stripShown.class)} />
                </label>
                <Textarea
                  id="note-class-hw-free"
                  value={classDraft.homework}
                  onChange={(e) => onClassHomeworkChange(e.target.value)}
                  className={cn('min-h-[40px]', textareaClass)}
                />
                <HomeworkPhotoStrip
                  ref={classStrip} photos={classDraft.photos} onChange={onClassPhotosChange}
                  sessionId={sessionId} tier="CLASS" scopeLabel={t('lightbox.common')} subtitle={photoSubtitle}
                  variant="below-textarea" onVisibilityChange={onClassStripShown}
                />
              </>
            )}
            {classHatch && (!classExtraShown ? (
              <button
                type="button"
                onClick={() => setClassExtraOpen(true)}
                className="mt-2.5 inline-flex min-h-[44px] items-center gap-1.5 px-1 text-[12.5px] font-medium text-brand-600 sm:min-h-[32px]"
              >
                <Plus className="h-[13px] w-[13px]" strokeWidth={2} />
                {t('notes.extra.add')}
              </button>
            ) : (
              <>
                {classUsesItems && (
                  <>
                    <label className="mb-1.5 mt-2.5 block text-[12.5px] font-medium text-surface-600" htmlFor="note-class-extra">
                      {t('notes.extra.label')}
                    </label>
                    <Textarea
                      id="note-class-extra"
                      value={classDraft.comment}
                      onChange={(e) => onClassCommentChange(e.target.value)}
                      className={cn('min-h-[48px]', textareaClass)}
                    />
                  </>
                )}
                {classUsesHw && (
                  <>
                    <label className="mb-1.5 mt-2.5 block text-[12.5px] font-medium text-surface-600" htmlFor="note-class-hw-extra">
                      {t('notes.extra.homework')}
                    </label>
                    <Textarea
                      id="note-class-hw-extra"
                      value={classDraft.homework}
                      onChange={(e) => onClassHomeworkChange(e.target.value)}
                      className={cn('min-h-[40px]', textareaClass)}
                    />
                  </>
                )}
              </>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="mb-2.5 mt-6 flex items-center gap-2.5">
          <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-surface-400">
            {t('notes.student.label')}
          </span>
          <span className="h-px flex-1 bg-surface-200" />
          <span className="text-[11.5px] text-surface-400">
            {writtenCount > 0 ? t('notes.student.hintCount', { count: writtenCount }) : t('notes.student.hint')}
          </span>
        </div>

        <div className="overflow-hidden rounded-lg border border-surface-200 bg-surface-0">
          {roster.map((s) => {
            const open = openStudentId === s.studentId;
            const draft: NoteDraft = students[s.studentId] ?? { comment: '', homework: '', items: {}, homeworkItems: {}, photos: [], rating: null };
            const orphans = orphansByStudent[s.studentId] ?? [];
            const parts = previewParts(draft, template, orphans);
            const displayName = s.studentNickname || s.studentName.trim().split(/\s+/).at(-1) || s.studentName;
            // Unique per student so a screen reader announces the right
            // label for the right field (review finding — these used to be
            // bare <label> with no id/htmlFor pairing).
            const commentId = `note-${s.studentId}-comment`;
            const homeworkId = `note-${s.studentId}-homework`;
            const hwOrphans = homeworkOrphansByStudent[s.studentId] ?? [];
            const studentUsesItems = hasTemplate && studentItems.length > 0;
            const studentUsesHw = hasTemplate && homeworkItems.length > 0;
            const studentHatch = studentUsesItems || studentUsesHw;
            const showRating = ratingEnabled && s.attendance !== 'ABSENT' && s.attendance !== 'EXCUSED';
            const extraShown = Boolean(extraOpen[s.studentId])
              || (studentUsesItems && Boolean(draft.comment.trim()))
              || (studentUsesHw && Boolean(draft.homework.trim()));
            return (
              <div key={s.studentId} className="border-b border-surface-100 last:border-b-0">
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => onToggleStudent(s.studentId)}
                  className={cn(
                    'flex min-h-[48px] w-full items-center gap-2.5 px-4 text-left',
                    s.attendance === 'ABSENT' && 'opacity-60',
                  )}
                >
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-[14px] text-surface-800', open ? 'font-semibold' : 'font-medium')}>
                      {s.studentName}
                      {s.studentNickname && <span className="font-normal text-surface-500"> ({s.studentNickname})</span>}
                      {showRating && draft.rating && (
                        <span className="ml-1.5 text-[15px] leading-none align-[-2px]">
                          <span aria-hidden="true">{sessionRatingMeta(draft.rating).emoji}</span>
                          <span className="sr-only">{sessionRatingMeta(draft.rating).label}</span>
                        </span>
                      )}
                      {parts.length > 0 && <MessageSquareText className="ml-1 inline h-[13px] w-[13px] text-brand-600" strokeWidth={2} />}
                      {photoCount(draft) > 0 && <ImageIcon className="ml-1 inline h-[13px] w-[13px] text-brand-600" strokeWidth={2} />}
                    </span>
                    {(parts.length > 0 || photoCount(draft) > 0) && (
                      <span className="block truncate text-[12.5px] text-surface-500">
                        {parts.map((p, i) => (
                          <span key={i}>
                            {i > 0 && ' · '}
                            {p.name && <span className="font-medium">{p.name}: </span>}
                            {p.text}
                          </span>
                        ))}
                        {photoCount(draft) > 0 && <span>{parts.length > 0 && ' · '}{t('notes.photos.count', { n: photoCount(draft) })}</span>}
                      </span>
                    )}
                  </span>
                  {/* THI-190 §11.2: a badge for EVERY marked student. Cycle 1 printed only
                      the exceptions to cut noise; the approved mock prints them all, and a
                      teacher scanning a roster reads "who was here" faster than "who was
                      not". PENDING has nothing to say and stays blank. */}
                  {s.attendance !== 'PENDING' && (
                    <span
                      className={cn(
                        'shrink-0 text-[12px]',
                        s.attendance === 'ABSENT' ? 'text-destructive'
                          : s.attendance === 'EXCUSED' ? 'text-attention-foreground'
                          : 'text-surface-500',
                      )}
                    >
                      {ts(`attendance.${s.attendance.toLowerCase()}` as 'attendance.present')}
                    </span>
                  )}
                  <ChevronDown className={cn('h-5 w-5 shrink-0 text-surface-400', open && 'rotate-180')} strokeWidth={2} />
                </button>

                {/* `hidden`, not unmounted: collapsing must never lose text. */}
                <div className={cn('bg-surface-50 px-4 pb-4', !open && 'hidden')}>
                  {/* THI-346: the level sits above everything written, the
                      first thing a teacher sets and the first thing a parent
                      sees. Only with the subject's switch on, and only for a
                      child who was there (the API never shows an absent or
                      excused child's rating: `visibleRating`). */}
                  {showRating && (
                    <div className="pt-3">
                      <p className="mb-1.5 text-[12.5px] font-medium text-surface-600">{t('notes.student.rating')}</p>
                      <SessionRatingPicker value={draft.rating} onChange={(v) => onStudentRatingChange(s.studentId, v)} />
                    </div>
                  )}
                  {studentUsesItems ? (
                    <div className="pt-3">
                      {/* Named like the homework table below it, with the one
                          fact a new teacher cannot see from the form: both
                          blocks reach the parent page and the AI monthly draft. */}
                      <p className="text-[12.5px] font-medium text-surface-600">{t('notes.student.items')}</p>
                      <FieldHint>{t('notes.student.itemsHint')}</FieldHint>
                      <NoteItemTable
                        items={studentItems}
                        values={draft.items}
                        onChange={(itemId, v) => onStudentItemChange(s.studentId, itemId, v)}
                        idPrefix={`note-${s.studentId}-item`}
                        orphans={orphans}
                        focusRef={focusStudentId === s.studentId ? focusRef : undefined}
                        focusItemId={studentItems[0]?.id ?? null}
                      />
                    </div>
                  ) : (
                    <>
                      {orphans.length > 0 && (
                        <div className="pt-3">
                          <NoteItemTable
                            items={[]}
                            values={draft.items}
                            onChange={(itemId, v) => onStudentItemChange(s.studentId, itemId, v)}
                            idPrefix={`note-${s.studentId}-item`}
                            orphans={orphans}
                          />
                        </div>
                      )}
                      <label className="block pt-3 text-[12.5px] font-medium text-surface-600" htmlFor={commentId}>
                        {t('notes.student.comment', { name: displayName })}
                      </label>
                      <FieldHint>{t('notes.student.itemsHint')}</FieldHint>
                      <Textarea
                        id={commentId}
                        ref={focusStudentId === s.studentId ? focusRef : undefined}
                        value={draft.comment}
                        onChange={(e) => onStudentCommentChange(s.studentId, e.target.value)}
                        placeholder={t('notes.student.commentPh', { name: displayName })}
                        className={cn('min-h-[100px]', textareaClass)}
                      />
                    </>
                  )}
                  {studentUsesHw ? (
                    <>
                      <div className="mt-2.5 flex min-h-6 items-center text-[12.5px] font-medium text-surface-600">
                        <span>{t('notes.homework.personal')}</span>
                        <HomeworkPhotoAddButton onOpen={() => studentStrips.current[s.studentId]?.openAdd()} planAllows={planAllowsPhotos} hidden={Boolean(stripShown[s.studentId])} />
                      </div>
                      <FieldHint>{t('notes.homework.personalHint')}</FieldHint>
                      <NoteItemTable
                        items={homeworkItems}
                        values={draft.homeworkItems}
                        onChange={(itemId, v) => onStudentHomeworkItemChange(s.studentId, itemId, v)}
                        idPrefix={`note-${s.studentId}-hw`}
                        orphans={hwOrphans}
                        footer={
                          <HomeworkPhotoStrip
                            ref={(h) => { studentStrips.current[s.studentId] = h; }}
                            photos={draft.photos} onChange={(next) => onStudentPhotosChange(s.studentId, next)}
                            sessionId={sessionId} tier="STUDENT" studentId={s.studentId}
                            scopeLabel={t('lightbox.personal', { name: displayName })} subtitle={photoSubtitle}
                            variant="card-row" onVisibilityChange={(v) => markShown(s.studentId, v)}
                          />
                        }
                      />
                    </>
                  ) : (
                    <>
                      {hwOrphans.length > 0 && (
                        <div className="mt-2.5">
                          <NoteItemTable
                            items={[]}
                            values={draft.homeworkItems}
                            onChange={(itemId, v) => onStudentHomeworkItemChange(s.studentId, itemId, v)}
                            idPrefix={`note-${s.studentId}-hw`}
                            orphans={hwOrphans}
                          />
                        </div>
                      )}
                      <label className="mt-2.5 flex min-h-6 items-center text-[12.5px] font-medium text-surface-600" htmlFor={homeworkId}>
                        <span>{t('notes.student.homework')}</span>
                        <HomeworkPhotoAddButton onOpen={() => studentStrips.current[s.studentId]?.openAdd()} planAllows={planAllowsPhotos} hidden={Boolean(stripShown[s.studentId])} />
                      </label>
                      <FieldHint>{t('notes.homework.freeHint')}</FieldHint>
                      <Textarea
                        id={homeworkId}
                        value={draft.homework}
                        onChange={(e) => onStudentHomeworkChange(s.studentId, e.target.value)}
                        placeholder={t('notes.student.homeworkPh')}
                        className={cn('min-h-[40px]', textareaClass)}
                      />
                      <HomeworkPhotoStrip
                        ref={(h) => { studentStrips.current[s.studentId] = h; }}
                        photos={draft.photos} onChange={(next) => onStudentPhotosChange(s.studentId, next)}
                        sessionId={sessionId} tier="STUDENT" studentId={s.studentId}
                        scopeLabel={t('lightbox.personal', { name: displayName })} subtitle={photoSubtitle}
                        variant="below-textarea" onVisibilityChange={(v) => markShown(s.studentId, v)}
                      />
                    </>
                  )}
                  {studentHatch && (!extraShown ? (
                    <button
                      type="button"
                      onClick={() => setExtraOpen((m) => ({ ...m, [s.studentId]: true }))}
                      className="mt-2.5 inline-flex min-h-[44px] items-center gap-1.5 px-1 text-[12.5px] font-medium text-brand-600 sm:min-h-[32px]"
                    >
                      <Plus className="h-[13px] w-[13px]" strokeWidth={2} />
                      {t('notes.extra.add')}
                    </button>
                  ) : (
                    <>
                      {studentUsesItems && (
                        <>
                          <label className="mb-1.5 mt-2.5 block text-[12.5px] font-medium text-surface-600" htmlFor={`${commentId}-extra`}>
                            {t('notes.extra.label')}
                          </label>
                          <Textarea
                            id={`${commentId}-extra`}
                            value={draft.comment}
                            onChange={(e) => onStudentCommentChange(s.studentId, e.target.value)}
                            className={cn('min-h-[66px]', textareaClass)}
                          />
                        </>
                      )}
                      {studentUsesHw && (
                        <>
                          <label className="mb-1.5 mt-2.5 block text-[12.5px] font-medium text-surface-600" htmlFor={`${homeworkId}-extra`}>
                            {t('notes.extra.homework')}
                          </label>
                          <Textarea
                            id={`${homeworkId}-extra`}
                            value={draft.homework}
                            onChange={(e) => onStudentHomeworkChange(s.studentId, e.target.value)}
                            className={cn('min-h-[40px]', textareaClass)}
                          />
                        </>
                      )}
                    </>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
